import { Box, Button, Card, Checkbox, CircularProgress, Divider, FormControlLabel, Grid, Paper, styled, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import WSSelectField from "../../Components/Select/WSSelect";
import WSTextField, { CurrencyType, TextType } from "../../Components/TextBox/WSTextBox";
import WSPhone from "../../Components/Phone/WSPhone";
import WSDateField from "../../Components/DateField/WSDateField";
import { callAPI, customValidation, getLocalValue, LoacalVariables, Method, validations } from "../../commonFunctions/CommonFunctions";
import dayjs from "dayjs";
import { HeaderList, IHeaderListActions } from "../../Components/Header/HeaderList";
import { WSIcons } from "../../commonFunctions/iIcons";
import { Info } from "@mui/icons-material";


interface District {
  _id: string;
  name: string;
}

interface Block {
  _id: string;
  name: string;
  code: string;
  district: District;
}

const Loan: React.FC<any> = React.memo((props) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [cdsList, setCdsList] = useState([]);

  const [blockData, setBlockData] = useState<Block | null>(null);
  const [loanStatus, setLoanStatus] = useState<string>("pending");
  const { showMessage } = useSnackbar();
  const { handleSubmit, control, watch, reset, setValue } = useForm();

  const navigate = useNavigate();
  const params = useParams();
  const id = params?.id;
  const userType = getLocalValue(LoacalVariables.UserType);
  const BlockId = getLocalValue(LoacalVariables.Block);

  useEffect(() => {
    const fetchDataById = async () => {
      setLoading(true);
      try {
        const response: any = await callAPI(
          "loans/getBy",
          Method.GET,
          {},
          id
        );

        if (response.success) {
          const loan = response.loan;

          setLoanStatus(loan.status)
          setValue("loanId", loan.loanId || "");
          setValue("applicantName", loan.applicantName || "");
          setValue("secondApplicantName", loan.secondApplicantName || "")
          setValue("isJointEntrepreneur", loan.secondApplicantName ? true : false)
          setValue("mobile", loan.mobile || "");
          setValue("whatsappNumber", loan.whatsappNumber || "");
          setValue("enterpriseName", loan.enterpriseName || "");
          setValue("nhgMemberName", loan.nhgMemberName || "");
          setValue("nhgName", loan.nhgName || "");
          setValue("adsName", loan.adsName || "");
          setValue("relationshipWithNhgMember", loan.relationshipWithNhgMember || "");
          setValue("sanctionedAmount", loan.sanctionedCefAmount || "")


          // setValue("loanType", loan.loanType?._id || "");
          setValue("appliedAmount", loan.appliedAmount || "");
          setValue("sanctionedDate", loan.sanctionedDate ? dayjs(loan.sanctionedDate).format("MM/DD/YYYY") : "")

          // setValue("amountReceived", loan.amountReceived || "");
          setValue("principal", loan.principal || "");
          setValue("interestRate", loan.interestRate || "");
          setValue("receiptNumber", loan.receiptNumber || "");

          setValue("cds", loan.cds?._id || "");
          setValue("cdsName", loan.cds?.name || "");
          // setValue("block", loan.block?._id || "");
          // setValue("district", loan.district?._id || "");

          setValue("referenceNumber", loan.referenceNumber || "");
          setValue("isGroupLoan", loan.isGroupLoan || false);

        }

      } catch (error: any) {
        console.error("Failed to retrieve data", error);
        if (error.message === 'GlobalHandledError') return;
        showMessage(
          error?.response?.data?.message || "Failed to retrieve data",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchDataById();
  }, [id]);


  useEffect(() => {
    const fetchBlockDataById = async () => {
      setLoading(true);
      try {
        const response: any = await callAPI(
          "blocks/getBy",
          Method.GET,
          {},
          BlockId
        );



        if (response.success) {
          setCdsList(response.data.cdsList);
          setBlockData(response.data.block);
        }

      } catch (error: any) {
        console.error("Failed to retrieve data", error);
        if (error.message === 'GlobalHandledError') return;
        showMessage(
          error?.response?.data?.message || "Failed to retrieve data",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };

    if (userType === 'block') fetchBlockDataById();
  }, []);

  // useEffect(() => {
  //   const fetchLoanTypes = async () => {
  //     setLoading(true);
  //     try {
  //       const response: any = await callAPI(
  //         "loanTypes/getAll",
  //         Method.GET,
  //       );

  //       if (response.success) {
  //         setloanTypeList(response.data)
  //       }

  //     } catch (error: any) {
  //       console.error("Failed to retrieve loan types", error);
  //       if (error.message === 'GlobalHandledError') return;
  //       showMessage(
  //         error?.response?.data?.message || "Failed to loan types",
  //         "error"
  //       );
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchLoanTypes();
  // }, [])


  const handleCancelClick = () => {
    navigate("/loan/requests/list");
  };


  const onSubmit = async (data: any) => {
    setSubmitting(true);

    try {
      const request = {
        ...data,
        block: blockData?._id,
        district: blockData?.district?._id,
      };

      const method = id ? Method.PUT : Method.POST;
      const url = id ? `loans/update/${id}` : "loans/create";

      const response: any = await callAPI(url, method, request);

      if (response.success) {
        showMessage(response.message, "success");
        navigate("/loan/requests/list");
        reset();
      }
    } catch (error: any) {
      if (error.message === 'GlobalHandledError') return;
      showMessage(
        error.response?.data.message || "An unexpected error occurred",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };



  const transformData = (data: any[]) => {
    return (data || []).map(item => ({
      label: item.name,
      value: item._id
    }));
  };



  const getButtonText = () => {
    if (submitting && id) return "Updating...";
    if (submitting) return "Creating...";
    if (id) return "Update Loan";
    return "Create Loan";
  };

  if (loading)
    return (
      <Box height="80vh" display="flex" alignItems="center" justifyContent="center">
        <CircularProgress sx={{ color: "#4AD06E" }} />
      </Box>
    );

  const actions: IHeaderListActions[] = [
    {
      type: "button",
      text: "Cancel",
      size: "small",
      onClick: handleCancelClick,
      variant: "outlined",
      color: "error",
      icon: <WSIcons.CloseIcon />,
      disabled: submitting,
    },
    {
      type: "button",
      text: "Save",
      size: "small",
      buttonType: "submit",
      variant: "outlined",
      color: "success",
      icon: <WSIcons.Save />,
      loading: submitting,
    }
  ];

  const renderFormContent = () => {
    if (loanStatus !== 'completed') {
      return (
        <form onSubmit={handleSubmit(onSubmit)}>
          <PremiumCard>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: "#022324",
                    background: 'linear-gradient(90deg, #022324 0%, #0A4A4D 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  New Loan Request
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                  Complete all sections to submit your application
                </Typography>
              </Grid>

              <Grid item>
                <Box display="flex" gap={2}>
                  <OutlineButton onClick={handleCancelClick}>
                    Cancel
                  </OutlineButton>
                  <GradientButton
                    type="submit"
                    variant="contained"
                    disabled={submitting}
                  >
                    {getButtonText()}
                  </GradientButton>
                </Box>
              </Grid>
            </Grid>
          </PremiumCard>

          <FormContainer>
            <Grid container spacing={4}>
              {/* Section A: Personal / Entrepreneur Details */}
              <Grid item xs={12}>
                <SectionHeader variant="h6">
                  A. Personal / Entrepreneur Details
                </SectionHeader>
              </Grid>

              <Grid item xs={12} sm={6}>
                <WSTextField
                  id="loanId"
                  control={control}
                  name="loanId"
                  label="Loan ID Number *"
                  rules={{
                    required: true,
                    minLength: {
                      value: 3,
                      message: "Loan ID must be at least 3 characters long.",
                    },
                  }}
                  variant="outlined"

                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <WSTextField
                  id="applicantName"
                  control={control}
                  name="applicantName"
                  label="Full Name of Entrepreneur *"
                  textIcon="Person"
                  rules={{ required: true }}
                  variant="outlined"

                />
              </Grid>

              {watch("isJointEntrepreneur") && (
                <Grid item xs={12} sm={6}>
                  <WSTextField
                    id="secondApplicantName"
                    control={control}
                    name="secondApplicantName"
                    label="Second Entrepreneur Name *"
                    rules={{ required: true }}
                    textIcon="Person"
                    variant="outlined"

                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Controller
                      name="isJointEntrepreneur"
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <Checkbox
                          {...field}
                          sx={{
                            color: '#4AD16E',
                            '&.Mui-checked': {
                              color: '#4AD16E',
                            },
                          }}
                        />
                      )}
                    />
                  }
                  label="Is there a second entrepreneur?"
                  sx={{ color: "#022324" }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <WSTextField
                  id="mobile"
                  label="Contact Number"
                  type={TextType.AllowOnlyNumber}
                  name="mobile"
                  control={control}
                  rules={customValidation(true, validations.phone)}
                  textIcon="Call"
                  disabled={loading}
                  variant="outlined"

                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <WSTextField
                  id="whatsappNumber"
                  label="WhatsApp Number"
                  type={TextType.AllowOnlyNumber}
                  name="whatsappNumber"
                  control={control}
                  rules={customValidation(true, validations.phone)}
                  textIcon="WhatsApp"
                  disabled={loading}
                  variant="outlined"

                />
              </Grid>

              <Grid item xs={12}>
                <WSTextField
                  id="enterpriceName"
                  control={control}
                  name="enterpriseName"
                  label="Name of Enterprise *"
                  rules={{ required: true }}
                  textIcon="Person"
                  variant="outlined"

                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>

              {/* Section B: NHG/CDS Membership Details */}
              <Grid item xs={12}>
                <SectionHeader variant="h6">
                  B. NHG / ADS / CDS Membership Details
                </SectionHeader>
              </Grid>

              <Grid item xs={12} sm={4}>
                <WSTextField
                  id="nhgMemberName"
                  control={control}
                  name="nhgMemberName"
                  label="NHG Member Name *"
                  rules={{ required: true }}
                  textIcon="Person"
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <WSTextField
                  id="relationshipWithNhgMember"
                  control={control}
                  name="relationshipWithNhgMember"
                  label="Relationship with Member *"
                  rules={{ required: true }}
                  textIcon="People"
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <WSTextField
                  id="nhgName"
                  control={control}
                  name="nhgName"
                  label="Name of NHG *"
                  rules={{ required: true }}
                  textIcon="Person"
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <WSTextField
                  id="adsName"
                  control={control}
                  name="adsName"
                  label="Name of ADS *"
                  rules={{ required: true }}
                  textIcon="Person"
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <WSSelectField
                  id="cds"
                  name="cds"
                  label={"Select CDS *"}
                  options={transformData(cdsList)}
                  control={control}
                  rules={{ required: true }}
                  loader={loading}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>

              {/* Section C: Loan Details */}
              <Grid item xs={12}>
                <SectionHeader variant="h6">
                  C. Loan Details
                </SectionHeader>
              </Grid>

              <Grid item xs={12} sm={6}>
                <WSTextField
                  id="appliedAmount"
                  control={control}
                  name="appliedAmount"
                  label="Applied CEF Amount"
                  type={TextType.Currency}
                  currency={CurrencyType.Rupee}
                  disabled={loanStatus === 'sanctioned' || loanStatus === 'disbursed'}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Controller
                      name="hasMoratorium"
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <Checkbox
                          {...field}
                          checked={field.value}
                          sx={{
                            color: '#4AD16E',
                            '&.Mui-checked': {
                              color: '#4AD16E',
                            },
                          }}
                        />
                      )}
                    />
                  }
                  label="Apply Moratorium Period (No EMI/Interest for initial months)"
                  sx={{ color: "#022324", fontWeight: 500 }}
                />
              </Grid>

              {watch("hasMoratorium") && (
                <Grid item xs={12} sm={6}>
                  <WSTextField
                    id="moratoriumMonths"
                    control={control}
                    name="moratoriumMonths"
                    label="Moratorium Period (Months)"
                    type={TextType.AllowOnlyNumber}
                    rules={{
                      required: watch("hasMoratorium"),
                      min: { value: 1, message: "Moratorium must be at least 1 month" },
                      max: { value: 12, message: "Maximum moratorium period is 12 months" }
                    }}

                    variant="outlined"
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Controller
                      name="isGroupLoan"
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <Checkbox
                          {...field}
                          sx={{
                            color: '#4AD16E',
                            '&.Mui-checked': {
                              color: '#4AD16E',
                            },
                          }}
                        />
                      )}
                    />
                  }
                  label="Loan for a group?"
                  sx={{ color: "#022324" }}
                />
              </Grid>

              {watch("hasMoratorium") && watch("moratoriumMonths") > 0 && (
                <Grid item xs={12}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      backgroundColor: '#E8F5E8',
                      border: '1px solid #4AD16E',
                      borderRadius: 2
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Info color="info" />
                      <Typography variant="body2" color="text.secondary">
                        <strong>Moratorium Applied:</strong> No EMI payments or interest for first {watch("moratoriumMonths")} months.
                        EMI will start from month {Number(watch("moratoriumMonths")) + 1}.
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </FormContainer>
          <Box sx={{ height: 60 }} />
        </form>
      );
    } else {
      return (
        <>
          <HeaderList title="Time to finalize the Loan" type="simple" actions={actions} isBackButton />
          <Grid
            item
            container
            spacing={1}
            justifyContent={"start"}
            sx={{
              marginTop: 5,
              marginLeft: 0,
              width: "100%",
              backgroundColor: "crimson",
              borderTopLeftRadius: 5,
              borderTopRightRadius: 5
            }}
          >
            <Grid
              alignContent={"center"}
              style={{ paddingLeft: 20, paddingTop: 0, height: "3rem" }}
              item
              sm={4}
              className="ws-data-grid-lightviolet-title"
            >
              <Typography variant="h6" id="tableTitle" sx={{ fontSize: "1rem" }}>
                Loan Information Disabled;
              </Typography>
            </Grid>
          </Grid>

          <Grid sx={{
            padding: 3,
            backgroundColor: "#fff",
            borderBottomLeftRadius: 5,
            borderBottomRightRadius: 5,
            border: "1px solid lightgrey"
          }}>
            <Grid container spacing={2} mt={1}><Grid item xs={12}>
              <Typography variant="h6" fontWeight="bold" color="crimson">
                Sorry Closed Loans cannot be edited.
              </Typography>
            </Grid>
            </Grid>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backdropFilter: 'blur(2px)',
                backgroundColor: 'rgba(255,255,255,0.3)',
                zIndex: 1,
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                padding: 2,
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, color: 'black' }}>
                Sorry Closed Loans cannot be edited.
              </Typography>
              <Button
                variant="contained"
                onClick={() => window.history.back()}
                sx={{ zIndex: 2 }}
              >
                Back
              </Button>
            </Box>
          </Grid>
        </>)
    }
  };

  return <>{renderFormContent()}</>;
});

export default Loan;

// Styled components for premium look
const PremiumCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
  borderRadius: '12px',
  padding: theme.spacing(3),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(255, 255, 255, 0.6)',
  backdropFilter: 'blur(10px)',
  marginBottom: theme.spacing(3),
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: '#022324',
  marginBottom: theme.spacing(2),
  paddingBottom: theme.spacing(1),
  borderBottom: '2px solid #4AD16F',
  display: 'inline-block',
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(90deg, #4AD16E 0%, #08B1B6 100%)',
  color: 'white',
  borderRadius: '8px',
  padding: '8px 24px',
  fontWeight: 500,
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(74, 209, 110, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 6px 16px rgba(74, 209, 110, 0.4)',
    transform: 'translateY(-2px)',
    background: 'linear-gradient(90deg, #43C367 0%, #07A0A5 100%)',
  },
  '&.Mui-disabled': {
    background: 'rgba(0, 0, 0, 0.12)',
  },
}));

const OutlineButton = styled(Button)(({ theme }) => ({
  border: '1px solid',
  borderColor: '#4AD16E',
  color: '#4AD16E',
  borderRadius: '8px',
  padding: '8px 24px',
  fontWeight: 500,
  textTransform: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#3AB85D',
    backgroundColor: 'rgba(74, 209, 110, 0.08)',
  },
}));

const FormContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#fafbfc',
  borderRadius: '12px',
  border: '1px solid rgba(0, 0, 0, 0.05)',
}));